import { defineStore } from 'pinia';
import { useModuleStore } from './moduleStore';
import { useBookingSystemStore } from './bookingSystemStore';
import { useAuthAdminStore } from './authAdminStore';
import AdminService from '@/services/admin.service';
import { useInscriptionVIPStore } from './inscriptionVIPStore';
import { useNotificationStore } from './notificationStore';
import { useAuthUserStore } from './authUserStore';
import { useChampsFormulaireStore } from './champsFormulaireStore';
import {
    requiresRFR26CodePostalCheck,
    setRFR26CodePostalValidated,
    validateRFR26CodePostal,
} from '@/utils/rfr26InscriptionGate';
import {
    requiresAtlantic27CodePostalCheck,
    setAtlantic27CodePostalValidated,
    setAtlantic27MaxiraceSelected,
    validateAtlantic27CodePostal,
} from '@/utils/atlantic27InscriptionGate';




export const useMainStore = defineStore('main', {
    state: () => ({
        module: useModuleStore(),
        champs_formulaire: useChampsFormulaireStore(),
        bookingSystem: useBookingSystemStore(),
        authAdmin: useAuthAdminStore(),
        authUser: useAuthUserStore(),
        billetterie_mode: false,
        parcours_billetterie_mode: false,
        dossard_bourse_mode: false,
        view_as_admin: false,
        inscription_as_admin: false,
        inscription_vip: useInscriptionVIPStore(),
        notification: useNotificationStore(),
        loading: false,
        rfr26InscriptionGate: {
            showDialog: false,
            pendingCallback: null,
            showRejectedMessage: false,
        },
        atlantic27InscriptionGate: {
            showDialog: false,
            pendingCallback: null,
            showRejectedMessage: false,
        },

    }),
    getters: {
        is_default_mode() {
            return !this.billetterie_mode && !this.parcours_billetterie_mode;
        },
        is_inscription_vip() {
            if (!this.inscription_vip) { return false; }
            return this.inscription_vip.is_valid();
        }
    },
    actions: {


        /**
         * Initializes the application by setting the module base, fetching data, and 
         * checking for specific metadata to set the parcours billetterie mode.
         * 
         * @param {string} [base=""] - The base URL or path for the module.
         * @returns {Promise<void>} - A promise that resolves when the initialization is complete.
         */
        async initializeApp(base = "", override_cache_age = null) {
            this.loading = true;
            this.dossard_bourse_mode = false;
            this.setModuleBase(base);
            await this.module.fetchData(override_cache_age);
            this.setAppStyle();

            if (this.module.metadatas?.module_parcours_billetterie == "1") {
                this.parcours_billetterie_mode = true;
            }
            if (this.module.metadatas?.module_billetterie == '1') {
                this.billetterie_mode = true;
            }
            this.loading = false;
        },

        /**
         * Initializes the view as admin, to check module even when closed.
         * 
         * This method checks if the current user has admin rights and sets the view accordingly.
         * If the user is a Sportips admin with super admin rights, the view is set to admin.
         * Otherwise, it validates the provided token to determine if the view should be set to admin.
         * 
         * @param {string} token - The token to validate for admin view.
         * @returns {Promise<void>} A promise that resolves when the view is set.
         */
        async init_view_as_admin(token) {
            if (this.authAdmin.isSportipsAdmin && this.authAdmin.hasSuperAdminRights) {
                this.view_as_admin = true;
                return;
            }
            let res = await AdminService.validTokenViewAsAdmin(token, this.module.base);
            this.view_as_admin = res;

        },

        /**
         * Initializes the inscription process as an admin.
         * 
         * It validates the provided token to determine if the user can view as an admin.
         * 
         * @param {string} token - The token to validate admin rights.
         * @returns {Promise<void>} - A promise that resolves when the initialization is complete.
         */
        async init_inscription_as_admin(token) {
            /* if (this.authAdmin.isSportipsAdmin && this.authAdmin.hasSuperAdminRights) {
                this.inscription_as_admin = true;
                return;
            } */
            let res = await AdminService.validTokenViewAsAdmin(token, this.module.base);
            this.inscription_as_admin = res;
        },

        async init_inscription_vip(token) {
            if (token == undefined) return;
            this.inscription_vip.token = token;
            this.inscription_vip.id_module = this.module.id;
            await this.inscription_vip.fetch();
        },

        async init_inscription_vip_multi_usage(token) {
            if (token == undefined) return;
            this.inscription_vip.token = token;
            this.inscription_vip.id_module = this.module.id;
            await this.inscription_vip.fetch_multi_usage();
        },


        /**
         * Sets the module base and updates the billetterie_mode flag.
         * If the base string includes "BILLETTERIE_", it sets billetterie_mode to true,
         * removes the "BILLETTERIE_" prefix from the base string, and assigns the result to this.module.base.
         * Otherwise, it sets billetterie_mode to false and assigns the base string directly to this.module.base.
         *
         * @param {string} base - The base string to set for the module.
         */
        setModuleBase(base) {
            if (base.includes("BILLETTERIE_")) {
                this.billetterie_mode = true;
                const base_module = base.replace("BILLETTERIE_", "");
                this.module.base = base_module;
            }
            else {
                this.billetterie_mode = false;
                this.module.base = base
            }
        },


        /**
         * Sets the background style of the application.
         * If the module's `url_background` is not an empty string,
         * it sets the background of the first element with the class "v-application"
         * to the specified URL.
         */
        setAppStyle() {
            if (this.module.url_background != '') {
                if (document.getElementsByClassName("v-application")[0]) {
                    document.getElementsByClassName("v-application")[0].style.background = "url('" + this.module.url_background + "')";
                }

            }
        },

        requestInscriptionAccess(callback) {
            this.requestRFR26InscriptionAccess(() => {
                this.requestAtlantic27InscriptionAccess(callback);
            });
        },

        requestRFR26InscriptionAccess(callback) {
            if (!requiresRFR26CodePostalCheck(this)) {
                callback();
                return;
            }
            this.rfr26InscriptionGate.showDialog = true;
            this.rfr26InscriptionGate.showRejectedMessage = false;
            this.rfr26InscriptionGate.pendingCallback = callback;
        },

        confirmRFR26CodePostal(codePostal) {
            if (validateRFR26CodePostal(codePostal)) {
                setRFR26CodePostalValidated();
                const callback = this.rfr26InscriptionGate.pendingCallback;
                this.closeRFR26InscriptionDialog();
                if (callback) {
                    callback();
                }
                return true;
            }
            this.rfr26InscriptionGate.showRejectedMessage = true;
            return false;
        },

        closeRFR26InscriptionDialog() {
            this.rfr26InscriptionGate.showDialog = false;
            this.rfr26InscriptionGate.pendingCallback = null;
            this.rfr26InscriptionGate.showRejectedMessage = false;
        },

        requestAtlantic27InscriptionAccess(callback) {
            if (!requiresAtlantic27CodePostalCheck(this)) {
                callback();
                return;
            }
            this.atlantic27InscriptionGate.showDialog = true;
            this.atlantic27InscriptionGate.showRejectedMessage = false;
            this.atlantic27InscriptionGate.pendingCallback = callback;
        },

        confirmAtlantic27CodePostal(codePostal) {
            if (validateAtlantic27CodePostal(codePostal)) {
                setAtlantic27CodePostalValidated();
                const callback = this.atlantic27InscriptionGate.pendingCallback;
                this.closeAtlantic27InscriptionDialog();
                if (callback) {
                    callback();
                }
                return true;
            }
            this.atlantic27InscriptionGate.showRejectedMessage = true;
            return false;
        },

        confirmAtlantic27Maxirace() {
            setAtlantic27MaxiraceSelected();
            const callback = this.atlantic27InscriptionGate.pendingCallback;
            this.closeAtlantic27InscriptionDialog();
            if (callback) {
                callback();
            }
        },

        closeAtlantic27InscriptionDialog() {
            this.atlantic27InscriptionGate.showDialog = false;
            this.atlantic27InscriptionGate.pendingCallback = null;
            this.atlantic27InscriptionGate.showRejectedMessage = false;
        },


    },
});